import React, { useEffect, useMemo, useState } from 'react';
import { Tag } from '@duxton-web/ui';
import debounce from 'lodash/debounce';

import CommonSelect, { CommonSelectProps } from './CommonSelect';
import { useGetTechFamilies } from '@/hooks/usePlannerQueries/tech-families';
import { TechFamilyItem } from '@/lib/PlannerMetadata';

const SEARCH_MIN_LENGTH = 2;
const DEBOUNCE_DELAY_MS = 300;

const TechFamilySelect = ({
  multiple,
  placeholder = 'Select tech family that owns this variable',
  ...props
}: Omit<CommonSelectProps, 'values' | 'idKey' | 'onSearch'>) => {
  const [debouncedSearch, setDebouncedSearch] = useState('');

  const debouncedSetSearch = useMemo(
    () =>
      debounce((value: string) => setDebouncedSearch(value), DEBOUNCE_DELAY_MS),
    [],
  );

  useEffect(() => {
    return () => {
      debouncedSetSearch.cancel();
    };
  }, [debouncedSetSearch]);

  const handleSearch = (value: string) => {
    if (value.length === 0 || value.length >= SEARCH_MIN_LENGTH) {
      debouncedSetSearch(value);
    } else {
      debouncedSetSearch.cancel();
      setDebouncedSearch('');
    }
  };

  const activeSearch =
    debouncedSearch.length >= SEARCH_MIN_LENGTH ? debouncedSearch : undefined;

  const { data: techFamiliesData } = useGetTechFamilies(activeSearch);
  const techFamilies = Array.isArray(techFamiliesData) ? techFamiliesData : [];

  const getTechFamilyLabel = (item: TechFamilyItem) =>
    item.helix_name?.trim() || item.name;

  const renderTechFamilyLabel = (item: TechFamilyItem) => {
    const label = getTechFamilyLabel(item);
    const isHelix = item.is_helix;

    return (
      <div className="common-select__tag-row">
        {label}
        <span data-filter-ignore>
          <Tag color={isHelix ? 'success' : 'default'} size="xsmall">
            {isHelix ? 'Helix' : 'GrabX'}
          </Tag>
        </span>
      </div>
    );
  };

  return (
    <CommonSelect
      {...{ placeholder, multiple, ...props }}
      values={techFamilies}
      idKey="name"
      onSearch={handleSearch}
      valueFn={renderTechFamilyLabel}
    />
  );
};

export default TechFamilySelect;
